import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2, MapPin, Star, ShieldCheck, Trophy, Home as HomeIcon,
  Hammer, CalendarClock, ArrowRight, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";

const formatSqft = (n) => {
  if (!n) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L+ sqft`;
  return `${n.toLocaleString("en-IN")} sqft`;
};

const initials = (name = "") =>
  name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const STAR_COLOR = "text-gold";

function DemoRibbon() {
  return (
    <div
      data-testid="builder-demo-ribbon"
      className="rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 flex items-start gap-2.5"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-none text-amber-600" />
      <div className="text-xs leading-relaxed">
        <span className="font-bold">Placeholder profile.</span> This builder page uses sample track-record and reviews. Real data will be added after the builder confirms it — nothing here is a live claim.
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5 flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center flex-none">
        <Icon className="w-5 h-5 text-forest" />
      </div>
      <div>
        <div className="text-2xl font-heading font-extrabold text-forest tracking-tight">{value}</div>
        <div className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function ProjectCard({ project }) {
  const inner = (
    <div className="rounded-xl border border-border bg-white overflow-hidden hover:border-gold transition-all">
      <div className="aspect-[16/10] bg-cream flex items-center justify-center overflow-hidden">
        {project.image_url ? (
          <img src={project.image_url} alt={project.name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-forest/40 text-4xl font-heading font-black">{initials(project.name)}</div>
        )}
      </div>
      <div className="p-4">
        <div className="font-heading font-bold text-forest text-base leading-tight">{project.name}</div>
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {project.location}
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {(project.unit_types || []).map((u) => (
            <span key={u} className="text-[10px] uppercase tracking-wider bg-forest/5 text-forest border border-forest/10 rounded-full px-2 py-0.5">
              {u}
            </span>
          ))}
        </div>
        {project.status_hint && (
          <div className="mt-2 text-xs text-muted-foreground italic">{project.status_hint}</div>
        )}
        {project.description && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{project.description}</p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-forest/70 font-medium">{project.year}</span>
          {project.linked_project_id && (
            <span className="text-[11px] text-gold font-bold inline-flex items-center gap-1">
              View listing <ArrowRight className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (project.linked_project_id) {
    return (
      <Link to={`/projects/${project.linked_project_id}`} data-testid={`builder-project-linked-${project.id}`}>
        {inner}
      </Link>
    );
  }
  return <div data-testid={`builder-project-${project.id}`}>{inner}</div>;
}

function ReviewCard({ review }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid={`builder-review-${review.id}`}>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`w-3.5 h-3.5 ${n <= (review.rating || 0) ? `${STAR_COLOR} fill-current` : "text-border"}`} />
        ))}
      </div>
      <p className="text-sm text-forest/90 mt-2 leading-relaxed">&ldquo;{review.quote}&rdquo;</p>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium text-forest/80">{review.reviewer_name}</span>
        <span>{review.date ? new Date(review.date).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : ""}</span>
      </div>
    </div>
  );
}

function ProjectGroup({ title, icon: Icon, projects }) {
  if (!projects?.length) return null;
  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center">
          <Icon className="w-4 h-4 text-forest" />
        </div>
        <h3 className="font-heading font-extrabold text-forest text-xl tracking-tight">{title}</h3>
        <span className="text-xs text-muted-foreground">({projects.length})</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
      </div>
    </div>
  );
}

export default function BuilderProfile() {
  const { idOrSlug } = useParams();
  const [builder, setBuilder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/builders/${idOrSlug}`);
        setBuilder(r.data);
      } catch {
        setBuilder(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [idOrSlug]);

  const totalProjects = useMemo(() => {
    if (!builder) return 0;
    return (builder.completed_projects?.length || 0)
      + (builder.ongoing_projects?.length || 0)
      + (builder.future_projects?.length || 0);
  }, [builder]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center text-muted-foreground">Loading builder…</div>;
  }
  if (!builder) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="font-heading font-bold text-2xl text-forest">Builder not found</div>
        <p className="text-muted-foreground mt-2 text-sm">This builder profile doesn&apos;t exist yet.</p>
        <Link to="/projects" className="inline-flex items-center gap-2 mt-6 rounded-full bg-forest text-white px-5 py-2.5 text-sm">
          Browse projects <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-cream/40" data-testid="builder-profile-page">
      {/* Header */}
      <section className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 md:py-16">
          {builder.is_demo && <div className="mb-5"><DemoRibbon /></div>}
          <div className="flex items-start gap-5 md:gap-7">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-forest text-white flex items-center justify-center font-heading font-black text-3xl md:text-4xl flex-none">
              {builder.logo_url
                ? <img src={builder.logo_url} alt={builder.name} className="w-full h-full object-cover rounded-2xl" />
                : <span>{initials(builder.name)}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold">Builder profile</div>
              <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-forest tracking-tight mt-1.5 leading-[1.05]" data-testid="builder-name">
                {builder.name}
              </h1>
              <div className="flex items-center flex-wrap gap-3 mt-3 text-sm text-forest/80">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {builder.location || "Hyderabad"}</span>
                <span className="text-forest/30">·</span>
                <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> {builder.years_experience}+ years in real estate</span>
                {builder.rera_registration && (
                  <>
                    <span className="text-forest/30">·</span>
                    <span data-testid="builder-rera" className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full bg-forest/5 text-forest border border-forest/10">
                      <ShieldCheck className="w-3 h-3" /> RERA {builder.rera_registration}
                    </span>
                  </>
                )}
              </div>
              {builder.tagline && (
                <p className="mt-4 text-base text-forest/85 max-w-2xl leading-relaxed">{builder.tagline}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <StatCard icon={CalendarClock} label="Years in business" value={`${builder.years_experience}+`} />
          <StatCard icon={Building2}    label="Projects (all-time)"  value={totalProjects} />
          <StatCard icon={HomeIcon}     label="Homes delivered"      value={builder.total_units_delivered ? `${builder.total_units_delivered}+` : "—"} />
          <StatCard icon={Trophy}       label="Area delivered"       value={formatSqft(builder.total_sqft_delivered)} />
        </div>
        {builder.about && (
          <div className="mt-8 rounded-2xl bg-white border border-border p-6 md:p-8">
            <h2 className="font-heading font-bold text-forest text-lg">About {builder.brand || builder.name}</h2>
            <p className="text-sm text-forest/80 mt-2 leading-relaxed">{builder.about}</p>
          </div>
        )}
      </section>

      {/* Projects */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="builder-projects-section">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold">Portfolio</div>
        <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-forest tracking-tight mt-2">Projects</h2>
        <ProjectGroup title="Ongoing" icon={Hammer} projects={builder.ongoing_projects} />
        <ProjectGroup title="Completed" icon={Trophy} projects={builder.completed_projects} />
        <ProjectGroup title="Upcoming" icon={CalendarClock} projects={builder.future_projects} />
      </section>

      {/* Reviews */}
      <section className="bg-white border-t border-border mt-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="builder-reviews-section">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold">What buyers say</div>
              <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-forest tracking-tight mt-2">Reviews</h2>
            </div>
            {builder.reviews?.some((r) => r.is_demo) && (
              <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2.5 py-1 font-bold">
                Sample reviews
              </span>
            )}
          </div>
          {!builder.reviews?.length ? (
            <div className="mt-6 rounded-xl border border-dashed border-border bg-cream p-8 text-center text-sm text-muted-foreground">
              No reviews yet.
            </div>
          ) : (
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {builder.reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-4">
            Live Google Reviews integration is planned. Until then, entries flagged &ldquo;Sample&rdquo; are placeholder data and won&apos;t be presented as claims about this builder.
          </p>
        </div>
      </section>
    </div>
  );
}
